import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useBook from "api/BookResource/useBook";
import { QUICK_LOOK_PARAM } from "./booksQueryParams";

export default function useCatalogueQuickLook(books) {
	const location = useLocation();
	const history = useHistory();
	const id = new URLSearchParams(location.search).get(QUICK_LOOK_PARAM);
	const selected = id !== null;
	const valid = /^[1-9]\d*$/.test(id || "") && Number.isSafeInteger(Number(id));
	const position = books.findIndex((book) => String(book.id) === id);
	const loaded = books[position];
	const details = useBook(valid ? id : null, { enabled: valid && !loaded, retry: false });
	const resolved = loaded || (valid && String(details.data?.id) === id ? details.data : null);
	const status = !selected || resolved ? null : !valid ? "invalid" : details.isError || details.isSuccess ? "error" : "loading";
	const book = !selected ? null : resolved || { id, title: status === "loading" ? "Loading book…" : "This book couldn’t load." };
	const open = useCallback((bookId) => {
		if (bookId == null) return;
		const current = history.location;
		const params = new URLSearchParams(current.search);
		const wasOpen = params.has(QUICK_LOOK_PARAM);
		if (params.get(QUICK_LOOK_PARAM) === String(bookId)) return;
		params.set(QUICK_LOOK_PARAM, bookId);
		// Keep adjacent books in the same history entry, so Back returns to browsing.
		history[wasOpen ? "replace" : "push"]({ ...current, search: `?${params}`, state: { ...current.state, catalogueQuickLook: wasOpen ? current.state?.catalogueQuickLook : true } });
	}, [history]);
	const close = useCallback(() => {
		const current = history.location;
		const params = new URLSearchParams(current.search);
		if (!params.has(QUICK_LOOK_PARAM)) return;
		if (current.state?.catalogueQuickLook) { history.goBack(); return; }
		// A shared link may have no catalogue entry behind it.
		params.delete(QUICK_LOOK_PARAM);
		history.replace({ ...current, search: params.toString() ? `?${params}` : "" });
	}, [history]);
	return { book, status, onRetry: valid ? details.refetch : undefined, position, count: position >= 0 ? books.length : 0, open, close };
}
